import React from 'react';
import { defineMessages } from 'react-intl';
import { useAlertCtaCore } from '../../hooks/useAlertCtaCore';
import { AlertCtaBanner } from './AlertCtaBanner';
import { Msg, useFormatMessage } from '../../i18n-review/client/Msg';

const CONSENT_SOURCE = 'job-detail-alert-cta';

const messages = defineMessages({
  bannerTextWithLocation: {
    id: 'jobDetailAlertCta.bannerTextWithLocation',
    defaultMessage: 'Never miss a new {role} job in {location}',
    description: 'Job-detail-page banner text offering alerts for similar roles in the same location as the current job',
  },
  bannerText: {
    id: 'jobDetailAlertCta.bannerText',
    defaultMessage: 'Never miss a new {role} job like this',
    description: 'Job-detail-page banner text offering alerts for similar roles, shown when the job has no concrete location to mention',
  },
  subText: {
    id: 'jobDetailAlertCta.subText',
    defaultMessage: 'Free weekly or daily digest. Unsubscribe anytime.',
    description: 'Small supporting line under the job-detail alert banner headline stating frequency, cost, and that it can be cancelled',
  },
  ctaButton: {
    id: 'jobDetailAlertCta.ctaButton',
    defaultMessage: 'Get email alerts',
    description: 'Button that opens (or triggers) job-alert setup for jobs like the one being viewed',
  },
  dismissLabel: {
    id: 'jobDetailAlertCta.dismissLabel',
    defaultMessage: 'Dismiss',
    description: 'Accessibility label for the close button on the job-detail alert CTA',
  },
  successText: {
    id: 'jobDetailAlertCta.successText',
    defaultMessage: "You're set. We'll email you about jobs like this.",
    description: 'Confirmation shown once alerts are enabled for jobs like the one being viewed',
  },
  manageLink: {
    id: 'jobDetailAlertCta.manageLink',
    defaultMessage: 'Manage alerts',
    description: 'Link from the job-detail alert success state to the saved-searches/alerts management page',
  },
  pendingText: {
    id: 'jobDetailAlertCta.pendingText',
    defaultMessage: 'Almost there: check your inbox to confirm your email and activate alerts.',
    description: 'Shown after enabling alerts when the account email still needs confirmation',
  },
  errorText: {
    id: 'jobDetailAlertCta.errorText',
    defaultMessage: "Something went wrong. We couldn't turn on alerts. Please try again.",
    description: 'Shown when enabling alerts for jobs like this fails',
  },
});

export interface JobDetailAlertCtaProps {
  /** The saved-search URL this offer targets: built by the caller from the job's own facets
   * via buildSimilarJobsSrpHref (the same helper the page's "see all similar jobs" link
   * already uses), not a live SRP querystring. The job-detail page has no search context of
   * its own (TEC-514 #1). */
  targetUrl: string;
  /** Display copy for the offer: the job's own normalised role title. Falsy hides the CTA
   * entirely (a handful of malformed listings have no role facet to build an alert around). */
  role?: string | null;
  /** First concrete location segment, same value the rest of the page already resolves via
   * getFirstConcreteLocationSegment. Omitted (not "Remote"/"Finland") entirely when absent. */
  primaryLocation?: string | null;
  /** The page's sign-in/feedback/report links (EngagementStrip, variant="minimal"), folded into
   * this card as a footer row instead of rendering as a second, separately-boxed CTA block right
   * underneath. The job-detail page previously stacked two full-weight cards (this one, plus
   * EngagementStrip) at the bottom, each with its own prominent pill buttons, competing for
   * attention. The caller is still responsible for rendering EngagementStrip on its own when
   * this component won't (no role, or not yet visible), so that page never loses that surface's
   * only sign-in/feedback/report entry point. */
  engagementFooter?: React.ReactNode;
  /** Company's real brand colour, editorial-rail redesign; undefined keeps the shared generic jc-accent green. */
  accentColor?: string;
}

/**
 * Job-detail-page counterpart to SrpAlertCta: same enable/guest-intent flow (useAlertCtaCore)
 * and the same AlertCtaBanner visual chrome, offered against a synthetic single-role search
 * rather than a live SRP querystring. Previously this page had zero alert-related UI at all. A
 * seeker reading one job had no path to "alert me on similar roles" without navigating back to
 * a search.
 */
export function JobDetailAlertCta({
  targetUrl,
  role,
  primaryLocation,
  engagementFooter,
  accentColor,
}: JobDetailAlertCtaProps): React.ReactElement | null {
  const formatMessage = useFormatMessage();

  const {
    shouldRender,
    busy,
    failed,
    isAuthenticated,
    alreadyActive,
    pendingConfirmation,
    uiLocale,
    handleDismiss,
    handleAuthenticatedClick,
    handleContinueAsGuest,
  } = useAlertCtaCore({ targetUrl, surface: 'job-detail', consentSource: CONSENT_SOURCE });

  // No role facet to build an alert around (rare: a handful of malformed listings), or the
  // core hook isn't ready to render yet (session-dismissed, auth still resolving, ...).
  if (!role || !shouldRender) {
    return null;
  }

  const bannerLabel = primaryLocation
    ? formatMessage(messages.bannerTextWithLocation, { role, location: primaryLocation })
    : formatMessage(messages.bannerText, { role });

  return (
    <AlertCtaBanner
      ariaLabel={bannerLabel}
      alreadyActive={alreadyActive}
      pendingConfirmation={pendingConfirmation}
      isAuthenticated={isAuthenticated}
      busy={busy}
      failed={failed}
      uiLocale={uiLocale}
      bannerText={bannerLabel}
      subText={<Msg {...messages.subText} />}
      successText={<Msg {...messages.successText} />}
      manageLinkLabel={formatMessage(messages.manageLink)}
      pendingText={<Msg {...messages.pendingText} />}
      toggleLabel={formatMessage(messages.ctaButton)}
      dismissLabel={formatMessage(messages.dismissLabel)}
      errorText={<Msg {...messages.errorText} />}
      footer={engagementFooter}
      accentColor={accentColor}
      onDismiss={handleDismiss}
      onAuthenticatedClick={handleAuthenticatedClick}
      onContinueAsGuest={handleContinueAsGuest}
    />
  );
}
